import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { ArtistsRoster } from '../components/TopArtistsChart';
import ArtistCreateDialog from '@/components/ArtistCreateDialog';
import { listArtists } from '@/features/artists/services/artists.api';
import type { IArtistRosterCard } from '@/types';

interface PageProps { theme: string; toggleTheme: () => void }

const ArtistsPage: React.FC<PageProps> = ({ theme, toggleTheme }) => {
  const [items, setItems] = useState<IArtistRosterCard[]>([]);
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [genre, setGenre] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => { reload(); }, []);
  useEffect(() => { if (params.get('new') === '1') setOpen(true); }, [params]);
  useEffect(() => { const t = window.setTimeout(()=> setDebouncedQ(q.trim()), 350); return ()=> window.clearTimeout(t); }, [q]);
  useEffect(() => { reload(); }, [debouncedQ, genre]);

  const reload = async () => {
    try {
      setLoading(true);
      const data = await listArtists(0, 50, { q: debouncedQ || undefined, genre: genre || undefined });
      const mapped: IArtistRosterCard[] = data.items.map(a => ({
        id: a.id,
        name: a.name,
        imgSrc: a.photoUrl || 'https://placehold.co/400x200?text=Artist',
        listeners: String(a.listeners ?? 0),
        change: 0,
      }));
      setItems(mapped);
    } catch { setItems([]); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-body">
      <TopNav theme={theme} toggleTheme={toggleTheme} />
      <main className="max-w-7xl mx-auto p-3 md:p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-extrabold text-primary">Artistas</h1>
            <p className="text-sm text-muted">Visão do roster e tendências.</p>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-green-600 text-white text-sm font-semibold hover:bg-green-700 active:scale-95"
          >
            <i className="ph ph-plus"></i> Adicionar artista
          </button>
        </div>
        <div className="mb-3 flex flex-col sm:flex-row gap-2">
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar artista..." className="px-3 py-2 rounded-md border border-default bg-transparent w-full sm:w-72" />
          <input value={genre} onChange={e=>setGenre(e.target.value)} placeholder="Filtrar gênero" className="px-3 py-2 rounded-md border border-default bg-transparent w-full sm:w-56" />
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_,i)=> (
              <div key={i} className="h-28 md:h-32 rounded-xl bg-subtle border border-default animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-surface p-6 rounded-lg border border-default text-center text-sm text-muted">Nenhum artista encontrado.</div>
        ) : (
          <ArtistsRoster artists={items} />
        )}
      </main>

      {open && (
        <ArtistCreateDialog open={open} onClose={() => setOpen(false)} onCreated={reload} />
      )}
    </div>
  );
};

export default ArtistsPage;


